import React from 'react';
import PropTypes from 'prop-types';
import querystring from 'querystring';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import defaultsDeep from 'lodash/defaultsDeep';
import find from 'lodash/find';
import get from 'lodash/get';
import flatMap from 'lodash/flatMap';
import mapValues from 'lodash/mapValues';
import size from 'lodash/size';
import values from 'lodash/values';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import { getPeopleMultiplier } from '../../../utils/getPeopleMultiplier';
import { setDataLayerObj } from '../../../utils/analytics';
import Hero from './Hero';
import Recommendation from './Recommendation';
import StartOver from './StartOver';
import { getLocationsData } from '../../../services/solutionBuilder.js';
import { getProductIdLocationsPrices } from '../../../services/solutionBuilder';

const defaultData = {
  ContactIcon: {
    value: {},
  },
  Context: {
    relatedLocation: '',
    Geolocations: [],
  },
  CountryPhone: {
    value: '',
  },
  ContactPhrase: {
    value: '',
  },
  Headline: {
    value: '',
  },
  SolutionOption: {
    AlternateCopy: {
      value: '',
    },
    AlternateFeatures: [],
    AlternateMatch: [],
    Copy: {
      value: '',
    },
    Features: [],
    Match: [],
  },
  Subtitle: {
    value: '',
  },
};

const PEOPLE_AMOUNT_WILDCARD = '{PeopleAmount}';
const ANALYTICS_EVENT_NAME = 'sbResults';

class SolutionBuilderResults extends SafePureComponent {
  fields = defaultsDeep(this.props.fields, defaultData);

  constructor(props) {
    super(props);
    this.state = {
      alternateLocationsList: {},
      alternatePrices: {},
      detailedAlternatePrices: [],
      detailedMatchPrices: [],
      loading: true,
      locationsList: {},
      mainLocationsPrices: {},
      peopleMultipliers: {},
    };
  }

  async componentDidMount() {
    let geoIds = '';
    let rawQueryParams;

    if (typeof window !== 'undefined') {
      rawQueryParams = window.location.search.substr(1);
      rawQueryParams = rawQueryParams ? querystring.parse(rawQueryParams) : {};
      geoIds = rawQueryParams.geoids || rawQueryParams.geoIds;
    }

    if (!geoIds) return;

    this.setAnalytics();
    await this.initPricesData(rawQueryParams, geoIds);
  }

  setAnalytics = () => {
    const dataLayerObject = {
      event: ANALYTICS_EVENT_NAME,
    };
    setDataLayerObj(dataLayerObject);
  };

  initPricesData = async (rawQueryParams, geoIds) => {
    const [matchPrices, alternatedPrices] = await Promise.all([
      this.getMatchPricesData(rawQueryParams, geoIds),
      this.getAlternatePricesData(rawQueryParams, geoIds),
    ]);

    this.setState({
      ...matchPrices,
      ...alternatedPrices,
      loading: false,
    });
  };

  getMatchPricesData = async (rawQueryParams, geoIds) => {
    const { Context, SolutionOption } = this.fields;
    const { Match } = SolutionOption;
    const { CountryIsoCode } = Context;
    const matchLocationsPromises = this.createLocationPromises(
      Match,
      CountryIsoCode,
      geoIds,
    );
    const { locationsList, prices, detailedPrices } = await this.getPricesData({
      locationPromises: matchLocationsPromises,
      match: Match,
      solutionsNumber: Match.length ? Match.length : 0,
      rawQueryParams: rawQueryParams,
      pricesParams: this.getPricesParams(Match),
    });
    const pricesData = await this.getLocationDataFromPrices(prices);
    return {
      locationsList: locationsList,
      mainLocationsPrices: pricesData,
      detailedMatchPrices: detailedPrices,
    };
  };

  getAlternatePricesData = async (rawQueryParams, geoIds) => {
    const { Context, SolutionOption } = this.fields;
    const { AlternateMatch } = SolutionOption;
    const { CountryIsoCode } = Context;

    if (!AlternateMatch || AlternateMatch.length === 0) return {};

    const alternateLocationsPromises = this.createLocationPromises(
      AlternateMatch,
      CountryIsoCode,
      geoIds,
    );
    const { locationsList, prices, detailedPrices } = await this.getPricesData({
      locationPromises: alternateLocationsPromises,
      match: AlternateMatch,
      solutionsNumber: AlternateMatch.length,
      rawQueryParams: rawQueryParams,
      pricesParams: this.getPricesParams(AlternateMatch),
    });
    const pricesData = await this.getLocationDataFromPrices(prices);

    return {
      alternateLocationsList: locationsList,
      alternatePrices: pricesData,
      detailedAlternatePrices: detailedPrices,
    };
  };

  getPricesData = async ({
    locationPromises,
    match,
    solutionsNumber,
    rawQueryParams,
    pricesParams,
  }) => {
    const responses = await Promise.all(locationPromises);
    const { locationsList, prices } = this.processPromiseResponses(
      responses,
      rawQueryParams,
      pricesParams,
      solutionsNumber,
    );

    if (!locationsList) return;

    const cheapestLocation = locationsList[prices.min.locationId];
    const detailedPrices = this.getDetailedPrices(
      rawQueryParams,
      pricesParams,
      cheapestLocation,
      match,
    );

    return { locationsList, prices, detailedPrices };
  };

  getLocationDataFromPrices = async prices => {
    const { sitecoreContext } = this.props;
    const language = get(sitecoreContext, 'route.itemLanguage', '');
    const locationsIds = values(prices).map(({ locationId }) => locationId);
    const { data } = await getLocationsData(locationsIds, language);
    const finalPrices = mapValues(prices, ({ locationId, price }) => {
      const fetchedLocation = find(data, { ProtonId: locationId });
      return { ...fetchedLocation, Price: price };
    });

    return finalPrices;
  };

  /**
   * This function iterates over locations array and calls getProductIdLocationsPrices API.
   * @param {array} locations list of locations.
   * @param {string} CountryIsoCode country base for the requests.
   * @param {string} geoIds list of geo solutions selected on solution builder wizzard.
   */
  createLocationPromises = (locations, CountryIsoCode, geoIds) => {
    return locations.map(item => {
      const {
        PriceUnit,
        ProductDayUsage,
        ContractLength,
        ProductId,
        ProductSubtypeId,
      } = item.fields;

      return getProductIdLocationsPrices(
        CountryIsoCode,
        geoIds,
        PriceUnit.value,
        ProductDayUsage.value,
        ContractLength.value,
        ProductId.value,
        ProductSubtypeId.value,
      );
    });
  };

  /**
   * This function creates an object of solutions that contains basic pricing parameters
   * like people multiplier param and premium value multiplier.
   * @param {array} match list of solutions that match with the solution builder selected options.
   * @returns {object} of solutions that contains basic pricing parameters
   */
  getPricesParams = match => {
    return match.reduce((acc, curr) => {
      const { fields } = curr;
      const hasPremium = get(fields, 'HasPremium.value');
      const productId = get(fields, 'ProductId.value');
      const productSubTypeId = parseInt(
        get(fields, 'ProductSubtypeId.value', ''),
      );

      const productNumber =
        !isNaN(productSubTypeId) && productSubTypeId !== 0
          ? productSubTypeId
          : productId;

      acc[productNumber] = acc[productNumber] || {};

      // default peopleValueParam to empty string to validate in price calculation
      acc[productNumber].peopleValueParam = get(
        fields,
        'PeopleValueParam.value',
        '',
      );

      if (hasPremium) {
        // default premiumValue to 1 because price is going to be multiplyed by it.
        acc[productNumber].premiumValue = parseInt(
          get(fields, 'PremiumValue.value', ''),
        );
      }
      return acc;
    }, {});
  };

  /**
   * This function performs all the calculations needed for the getProductIdLocationsPrices API call.
   * @param {array} responses list of promises responses
   * @param {object} rawQueryParams object containing all the query params
   * @param {object} pricesParams object with pricing parameters
   * @param {number} solutionsNumber amount of requests made to getProductIdLocationsPrices
   * @returns {object} locationValues containing structured locations list and prices for the getProductIdLocationsPrices API call.
   */
  processPromiseResponses = (
    responses,
    rawQueryParams,
    pricesParams,
    solutionsNumber,
  ) => {
    // groups the responses from each API call in a sinle array.
    const locations = flatMap(responses, response => response.data) || [];
    if (locations.length === 0) return {};
    let locationsList = this.processLocationsList(locations, solutionsNumber);

    for (let location in locationsList) {
      locationsList[location] = this.processLocationsPrice(
        locationsList[location],
        rawQueryParams,
        pricesParams,
      );
    }

    const prices = this.getLocationsMainPrices(locationsList);
    const locationValues = { locationsList, prices };
    return locationValues;
  };

  /**
   * @param {array} solutions list of all the solutions returned by the getProductIdLocationsPrices API.
   * This functions groups the solutions by solutionID and returns an object of locations.
   */
  processLocationsList = (solutions, solutionsNumber) => {
    const groupedSolutionsByLocation = solutions.reduce((acc, curr) => {
      acc[curr.LocationNumber] = acc[curr.LocationNumber] || {
        list: [],
        locationId: curr.LocationNumber,
      };
      acc[curr.LocationNumber]['list'] = acc[curr.LocationNumber]['list'] || [];
      acc[curr.LocationNumber]['list'].push(curr);
      return acc;
    }, {});

    // exclude the locations that don't match the amount of solutions the user requested.
    for (let location in groupedSolutionsByLocation) {
      if (groupedSolutionsByLocation[location].list.length < solutionsNumber) {
        delete groupedSolutionsByLocation[location];
      }
    }
    return groupedSolutionsByLocation;
  };

  /**
   * This function process rawQueryParams and creates an object for people multiplier paramteres.
   * @param {object} rawQueryParams object obtained from the query parameters
   * @returns values to multiply prices by.
   */
  generatePeopleMultiplier = rawQueryParams => {
    // default to 1 because the price is going to be multiplyed by it.
    return {
      dop: parseInt(rawQueryParams.dop) || '',
      omp: parseInt(rawQueryParams.omp) || '',
      ddp: parseInt(rawQueryParams.ddp) || '',
      cmp: parseInt(rawQueryParams.cmp) || '',
    };
  };

  /**
   * @param {object} location object of a location containing a list of solutions
   * @param {object} rawQueryParams query params
   * @param {object} pricesParams price parameters extracted from the match object in first load
   * This function returns the location with the price processed.
   * based on pricesParams the price calculation is done using those multipliers.
   */
  processLocationsPrice = (location, rawQueryParams, pricesParams) => {
    const peopleMultipliers = this.generatePeopleMultiplier(rawQueryParams);

    this.setState({ peopleMultipliers: peopleMultipliers });

    location.price = location.list.reduce((acc, location) => {
      let finalValue = 0;
      const productId = parseInt(location.ProductTypeId);
      const productVariantId = parseInt(location.ProductVariantId);
      const productNumber =
        !isNaN(productVariantId) && productVariantId !== 0
          ? productVariantId
          : productId;

      const peopleMultiplier = pricesParams[productNumber].peopleValueParam
        ? getPeopleMultiplier(
            pricesParams[productNumber].peopleValueParam,
            peopleMultipliers,
          )
        : 1;

      const premiumMultiplier =
        pricesParams[productNumber].premiumValue && peopleMultiplier === 1
          ? 1 + pricesParams[productNumber].premiumValue / 100
          : 1;

      finalValue = location.Value * peopleMultiplier * premiumMultiplier;

      return acc + finalValue;
    }, 0);
    return location;
  };

  /**
   * @param {object} locationsList object containing all the locations with the solutions related to it self.
   * This function calculates the minimum, maximum and middle price for all the locations
   * based on the already calculated price per locations.
   */
  getLocationsMainPrices = locationsList => {
    const prices = {};
    let locationsArray = [];

    // creates an array of locations to be able to sort and extract min and max easily.
    for (let location in locationsList) {
      locationsArray.push(locationsList[location]);
    }

    const middlePosition =
      locationsArray.length % 2 === 0
        ? locationsArray.length / 2
        : (locationsArray.length + 1) / 2 - 1;

    locationsArray.sort((a, b) => (a.price > b.price ? 1 : -1));

    prices.min = {
      locationId: locationsArray[0].locationId,
      price: locationsArray[0].price,
    };

    if (locationsArray.length > 1) {
      prices.max = {
        locationId: locationsArray[locationsArray.length - 1].locationId,
        price: locationsArray[locationsArray.length - 1].price,
      };

      if (locationsArray.length - 1 !== middlePosition) {
        prices.mid = {
          locationId: locationsArray[middlePosition].locationId,
          price: locationsArray[middlePosition].price,
        };
      }
    }

    return prices;
  };

  /**
   * This function process item price
   */
  getDetailedPrices = (
    rawQueryParams,
    pricesParams,
    cheapestLocation,
    Match,
  ) => {
    const peopleMultipliers = this.generatePeopleMultiplier(rawQueryParams);
    let services = [];
    if (cheapestLocation && cheapestLocation.list) {
      services = cheapestLocation.list.map(item => {
        const productNumber =
          !isNaN(item.ProductVariantId) && parseInt(item.ProductVariantId) !== 0
            ? item.ProductVariantId
            : item.ProductTypeId;

        const peopleMultiplier = pricesParams[productNumber].peopleValueParam
          ? getPeopleMultiplier(
              pricesParams[productNumber].peopleValueParam,
              peopleMultipliers,
            )
          : 1;

        const premiumMultiplier =
          pricesParams[productNumber].premiumValue && peopleMultiplier === 1
            ? 1 + pricesParams[productNumber].premiumValue / 100
            : 1;

        // gets solution descriptive fields for side panel
        const solution = Match.find(
          solution =>
            solution.fields.ProductId.value === item.ProductTypeId &&
            solution.fields.ProductSubtypeId.value === item.ProductVariantId,
        );
        const {
          DiscountText,
          PriceResultCopy,
          SidePanelText,
        } = solution.fields;
        const finalValue = item.Value * peopleMultiplier * premiumMultiplier;
        const peopleAmount =
          peopleMultiplier !== 1 ? `${peopleMultiplier}x` : '';
        const solutionDescription = SidePanelText.value.replace(
          PEOPLE_AMOUNT_WILDCARD,
          peopleAmount,
        );

        return {
          price: finalValue,
          priceUnit: item.PriceUnitId,
          currency: item.CurrencyId,
          description: solutionDescription,
          discountText: DiscountText.value,
          pricesBlockTitle: PriceResultCopy.value,
        };
      });
    }
    return services;
  };

  getRecommendationsFields() {
    const { fields } = this;
    const RelatedLocationsLink = get(fields, 'Context.relatedLocations', '');
    const {
      AlternateCopy,
      AlternateFeatures,
      AlternateMatch,
      AlternateTag,
      Copy,
      Features,
      Match,
      Tag,
    } = fields.SolutionOption;
    const { NearbyLocationsTitle } = fields;
    const Geolocations = get(fields, 'Context.Geolocations', []).join(', ');
    const commonFields = {
      Geolocations,
      NearbyLocationsTitle,
      RelatedLocationsLink,
    };

    return {
      ExactRecommendation: {
        ...commonFields,
        AlternateTag,
        Copy,
        Features,
        Match,
        Tag,
      },
      AlternateRecommendation: {
        ...commonFields,
        AlternateTag,
        Copy: AlternateCopy,
        Features: AlternateFeatures,
        isAlternateMatch: true,
        Match: [...AlternateMatch],
        Tag,
      },
    };
  }

  safeRender() {
    const { fields } = this;
    const {
      alternatePrices,
      detailedAlternatePrices,
      detailedMatchPrices,
      loading,
      locationsList,
      mainLocationsPrices,
      peopleMultipliers,
    } = this.state;

    const {
      ExactRecommendation,
      AlternateRecommendation,
    } = this.getRecommendationsFields();

    return (
      <div>
        <Hero
          {...fields}
          availableLocation={size(mainLocationsPrices)}
          totalLocation={size(locationsList)}
        />
        <section>
          <Recommendation
            fields={ExactRecommendation}
            locationsPrices={mainLocationsPrices}
            detailedPrices={detailedMatchPrices}
            loading={loading}
            peopleMultiplier={peopleMultipliers}
          />
          {AlternateRecommendation && (
            <Recommendation
              fields={AlternateRecommendation}
              locationsPrices={alternatePrices}
              detailedPrices={detailedAlternatePrices}
              loading={loading}
              peopleMultiplier={peopleMultipliers}
            />
          )}
        </section>
        <StartOver />
      </div>
    );
  }
}

const objectValueType = {
  value: PropTypes.string,
};

const objectLocationType = {
  fields: {
    Text: PropTypes.shape(objectValueType),
  },
};

const featureType = {
  fields: PropTypes.shape({
    Icon: PropTypes.shape({
      value: PropTypes.shape({
        alt: PropTypes.string,
        height: PropTypes.string,
        src: PropTypes.string,
        width: PropTypes.string,
      }),
    }),
    Label: PropTypes.shape(objectValueType),
  }),
};

SolutionBuilderResults.displayName = 'SolutionBuilderResults';

SolutionBuilderResults.defaultProps = {
  fields: defaultData,
};

SolutionBuilderResults.propTypes = {
  ContactIcon: PropTypes.shape(objectValueType),
  ContactPhrase: PropTypes.shape(objectValueType),
  Context: PropTypes.shape({
    Geolocations: PropTypes.arrayOf(PropTypes.string),
    relatedLocation: PropTypes.string,
  }),
  CountryPhone: PropTypes.shape(objectValueType),
  Headline: PropTypes.shape(objectValueType),
  SolutionOption: PropTypes.shape({
    AlternateCopy: PropTypes.shape(objectValueType),
    AlternateFeatures: PropTypes.arrayOf(PropTypes.shape(featureType)),
    AlternateMatch: PropTypes.arrayOf(
      PropTypes.shape(PropTypes.shape(objectLocationType)),
    ),
    Copy: PropTypes.shape(objectValueType),
    Features: PropTypes.arrayOf(PropTypes.shape(featureType)),
    Match: PropTypes.arrayOf(PropTypes.shape(objectLocationType)),
  }),
  Subtitle: PropTypes.shape(objectValueType),
};

export default withSitecoreContext()(SolutionBuilderResults);
